import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, Card, Spinner, EmptyState, JobIdentity } from "@/components/bits";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Loader2, PackageMinus } from "lucide-react";

export default function PartKeluar() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [job, setJob] = useState(null);
  const [issue, setIssue] = useState(null); // form for a part
  const [saving, setSaving] = useState(false);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadIssues = () => { setLoading(true); api.get("/issue").then((r) => setIssues(r.data)).finally(() => setLoading(false)); };
  useEffect(loadIssues, []);

  const doSearch = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    const { data } = await api.get(`/search?q=${encodeURIComponent(q.trim())}`);
    setResults(data.jobs || []);
  };

  const openJob = async (jid) => {
    const { data } = await api.get(`/jobs/${jid}`);
    setJob(data);
    setResults([]);
  };

  const startIssue = (p) => setIssue({ part_number: p.part_number, part_name: p.part_name, qty: 1, recipient: "", notes: "", available: p.received_qty - p.issued_qty });

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/issue", { job_id: job.id, part_number: issue.part_number, part_name: issue.part_name, qty: Number(issue.qty), recipient: issue.recipient, notes: issue.notes });
      toast.success("Part keluar tercatat (ISSUED).");
      setIssue(null);
      const { data } = await api.get(`/jobs/${job.id}`); setJob(data);
      loadIssues();
    } catch (err) {
      toast.error(apiError(err, "Gagal issue"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader title="Part Keluar" subtitle="Keluarkan part yang sudah RECEIVED ke workshop. Tidak boleh melebihi available." />

      <Card className="p-4 mb-4">
        <form onSubmit={doSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input className="pl-9 h-11" placeholder="Cari Nopol / No Claim / No SPK" value={q} onChange={(e) => setQ(e.target.value)} data-testid="issue-search-input" />
          </div>
          <Button className="bg-gray-900 h-11" data-testid="issue-search-btn">Cari</Button>
        </form>
        {results.length > 0 && (
          <div className="mt-3 space-y-2">
            {results.map((j) => (
              <button key={j.id} onClick={() => openJob(j.id)} className="w-full text-left border border-gray-200 rounded-lg p-3 hover:bg-gray-50" data-testid={`issue-job-${j.id}`}>
                <JobIdentity job={j} />
              </button>
            ))}
          </div>
        )}
      </Card>

      {job && (
        <Card className="p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <JobIdentity job={job} />
            <StatusBadge status={job.status} />
          </div>
          <div className="divide-y divide-gray-100">
            {(job.required_parts || []).map((p) => {
              const avail = p.received_qty - p.issued_qty;
              return (
                <div key={p.id} className="py-2.5 flex items-center justify-between gap-2">
                  <div>
                    <div className="font-mono-data font-semibold text-gray-900">{p.part_number || "—"}</div>
                    <div className="text-sm text-gray-600">{p.part_name}</div>
                    <div className="text-xs text-gray-400 mt-0.5">Received {p.received_qty} • Issued {p.issued_qty} • Available <b>{avail}</b></div>
                  </div>
                  <Button size="sm" variant="outline" disabled={avail <= 0} onClick={() => startIssue(p)} data-testid={`issue-part-${p.id}`}>
                    <PackageMinus size={14} className="mr-1" /> Keluarkan
                  </Button>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <h3 className="font-head font-semibold text-gray-900 mb-2">Riwayat Part Keluar</h3>
      {loading ? <Spinner /> : issues.length === 0 ? <EmptyState title="Belum ada part keluar" icon={PackageMinus} /> : (
        <div className="grid gap-2">
          {issues.map((it) => (
            <Card key={it.id} className="p-3 flex items-center justify-between">
              <div>
                <div className="font-mono-data font-semibold">{it.part_number} × {it.qty}</div>
                <div className="text-xs text-gray-400">{it.nopol} • {it.recipient || "—"} • {it.created_by}</div>
              </div>
              <StatusBadge status={it.installed ? "INSTALLED" : "ISSUED"} />
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!issue} onOpenChange={(o) => !o && setIssue(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head">Keluarkan Part</DialogTitle></DialogHeader>
          {issue && (
            <div className="space-y-3">
              <div className="text-sm"><b className="font-mono-data">{issue.part_number}</b> — {issue.part_name}</div>
              <div className="text-xs text-gray-500">Available: {issue.available}</div>
              <div>
                <label className="text-xs font-semibold uppercase text-gray-500">Qty</label>
                <Input type="number" min={1} max={issue.available} value={issue.qty} onChange={(e) => setIssue({ ...issue, qty: e.target.value })} className="mt-1" data-testid="issue-qty" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-gray-500">Penerima / Teknisi</label>
                <Input value={issue.recipient} onChange={(e) => setIssue({ ...issue, recipient: e.target.value })} className="mt-1" data-testid="issue-recipient" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-gray-500">Notes</label>
                <Input value={issue.notes} onChange={(e) => setIssue({ ...issue, notes: e.target.value })} className="mt-1" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIssue(null)}>Batal</Button>
            <Button onClick={submit} disabled={saving} className="bg-gray-900" data-testid="confirm-issue-btn">{saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null} Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
